import {
  HMSPeer,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectIsSomeoneScreenSharing,
  selectLocalPeer,
  selectPeerByCondition,
  selectPeersByCondition,
  selectScreenShareByPeerID,
  useHMSActions,
  useHMSStore,
  useVideo,
  useAVToggle,
  selectPeersByRole,
  selectIsConnectedToRoom,
  selectPeerMetadata,
  useCustomEvent,
  DeviceType,
  useDevices,
  HMSRoomState,
  selectRoomState,
  selectSessionStore,
} from "@100mslive/react-sdk";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState, memo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Child } from "../../api/child";
import { useGlobalStore } from "../../api/globalStores";
import noVideo from "../../assets/no-video.png";
import ButtonPrimary from "../../Common/Buttons/ButtonPrimary";
import BaseCard from "../../Common/Cards/BaseCard";
import IconHand from "../../Common/Icons/IconHand";
import IconMic from "../../Common/Icons/IconMic";
import IconMoreHorizontal from "../../Common/Icons/IconMoreHorizontal";
import IconRecord from "../../Common/Icons/IconRecord";
import Modal from "../../Common/Utility/Modal";
import EngagementPanelComponent from "../../Teacher/Components/EngagementPanelEditor/EngagementPanel";
import { StarRatingForm } from "../../Teacher/Components/TeacherFeedbackForm";
import AvatarGridItem from "../Components/AvatarGridItem";
import { HappyFriendsSVG } from "../Components/HappyFriendsSVG";
import {
  EngagementPanelResultString,
  HMSTalkingPartyPeerMetadata,
} from "../../Common/Utility/helpers";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImagePreviewModal from "../../Common/Utility/ImagePreviewModal";
import { EngagementPanel as EngagementPanelStore } from "../../api/engagementPanel";
import { supabase } from "../../api/supabase/client";
import { useHMSExceptionHandler } from "../../hooks/useHMSExceptionHandler";
import BrijLoadingIndicator from "../../Common/Misc/BrijLoadingIndicator";
import {
  getSharedAudioContext,
  resumeSharedAudioContext,
  unlockAudio,
} from "../../util/sharedAudioContext";

export interface IStudentLesson {}

export default observer(StudentLesson);

const logger = console;
const LOG_EP = (msg: string, extra?: any) =>
  logger.log(`[EP-RT] ${msg}`, extra ?? "");
const REALTIME_HEALTH_INTERVAL_MS = 15000;

// iOS device and browser detection utilities
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (
    navigator.userAgent.includes("Mac") && "ontouchend" in document
  );
};

const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const isChromeOnIOS = () => {
  return isIOS() && /CriOS/i.test(navigator.userAgent);
};

const isMobileOrTablet = () => {
  return isIOS() || /Android/i.test(navigator.userAgent);
};

// Permission management for iOS
let permissionGranted = {
  microphone: false,
  camera: false
};

const checkMediaPermissions = async () => {
  if (!navigator.permissions) return { microphone: false, camera: false };

  try {
    const microphonePermission = await navigator.permissions.query({ name: 'microphone' as any });
    const cameraPermission = await navigator.permissions.query({ name: 'camera' as any });

    permissionGranted.microphone = microphonePermission.state === 'granted';
    permissionGranted.camera = cameraPermission.state === 'granted';

    return {
      microphone: microphonePermission.state === 'granted',
      camera: cameraPermission.state === 'granted'
    };
  } catch (error) {
    logger.error('Error checking permissions:', error);
    return { microphone: false, camera: false };
  }
};

const requestMediaPermissions = async (force = false) => {
  if (!isIOS()) return true;

  try {
    // Check existing permissions first
    const existingPermissions = await checkMediaPermissions();

    // If we already have permissions and not forcing, return early
    if (!force && existingPermissions.microphone && existingPermissions.camera) {
      logger.log('Permissions already granted');
      return true;
    }

    // For Safari, we need user gesture to request permissions
    if (isSafari() && !force) {
      logger.log('Safari detected - permissions will be requested on user interaction');
      return false; // Let the UI handle permission requests
    }

    // Request permissions
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true
    });

    // Stop the stream immediately - we just needed it for permissions
    stream.getTracks().forEach(track => track.stop());

    permissionGranted.microphone = true;
    permissionGranted.camera = true;

    logger.log('Media permissions granted');
    return true;
  } catch (error) {
    logger.error('Failed to get media permissions:', error);
    return false;
  }
};

/**
 * Initialise audio for iOS using the shared AudioContext singleton.
 * Previous code created a new AudioContext + RTCPeerConnection every call,
 * leaking resources and eventually breaking audio on low-end iPads.
 */
const initializeIOSAudio = async () => {
  if (!isIOS()) return;
  try {
    await unlockAudio(); // resume shared ctx + play silent buffer
    logger.log("iOS audio session initialized (shared context)");
  } catch (error) {
    logger.error("Failed to initialize iOS audio:", error);
  }
};

//TODO: When openning student lesson from My Schedule should throw error if fail, (Prper toast promise)
function StudentLesson() {
  const navigate = useNavigate();
  const globalStores = useGlobalStore();

  // Release heap-heavy lesson state when the component unmounts
  // (e.g. student navigates to dashboard via any path).
  useEffect(() => {
    return () => {
      globalStores.curTalkingParty.cleanupSessionMemory();
    };
  }, []);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const [rtResubscribeTick, setRtResubscribeTick] = useState(0);
  const [quizToggle, setQuizToggle] = useState(true);
  const [openMoreMenu, setOpenMoreMenu] = useState(false);
  const [initialMuteDone, setInitialMuteDone] = useState(false);
  // const teacherScreenShared = useHMSStore(selectIsSomeoneScreenSharing);
  const hmsActions = useHMSActions();
  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } =
    useAVToggle();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const roomState = useHMSStore(selectRoomState);
  const isReconnecting = roomState === HMSRoomState.Reconnecting;
  const [showRestartButton, setShowRestartButton] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isReconnecting) {
      timer = setTimeout(() => {
        setShowRestartButton(true);
      }, 10000);
    } else {
      setShowRestartButton(false);
    }
    return () => clearTimeout(timer);
  }, [isReconnecting]);

  const handleManualRestart = async () => {
    try {
      toast.loading("Restarting connection...");
      await hmsActions.leave();
      await globalStores.curTalkingParty.joinRoom();
      toast.success("Connection restarted");
    } catch (error) {
      logger.error("Manual restart failed", error);
      toast.error("Restart failed. Please refresh the page.");
    }
  };

  useHMSExceptionHandler();

  const [currentOptionImagePreview, setCurrentOptionImagePreview] = useState<
    keyof EngagementPanelStore["assets"] | undefined
  >();
  const [isAnswering, setIsAnswering] = useState(false);

  const teacherPeer = useHMSStore(
    selectPeerByCondition((peer) => {
      if (!peer.customerUserId) return false;
      const parsedId = JSON.parse(peer.customerUserId);
      const isTeacher = parsedId.teacherId !== null;
      return isTeacher;
    })
  );

  // Enhanced debugging for teacher peer and audio tracks
  useEffect(() => {
    if (isChromeOnIOS()) {
      if (teacherPeer) {
        logger.log('🔊 CHROME IPAD DEBUG: Teacher peer detected:', {
          id: teacherPeer.id,
          name: teacherPeer.name,
          videoTrack: teacherPeer.videoTrack,
          audioTrack: teacherPeer.audioTrack
        });
      } else {
        logger.log('🔊 CHROME IPAD DEBUG: No teacher peer found');
      }
    }
  }, [teacherPeer]);

  console.log(`Teacher Peer is: ${teacherPeer?.videoTrack}`);
  const studentPeers = useHMSStore(
    selectPeersByCondition((peer) => {
      if (!peer.customerUserId) return false;
      const parsedId = JSON.parse(peer.customerUserId);
      const isTeacher = parsedId.studentId !== null;
      return isTeacher;
    })
  );

  //not sure why IDs and some other properties are missing.
  // const teacherPeer = useHMSStore(selectPeersByRole("teacher"))
  // const studentPeers = useHMSStore(selectPeersByRole("student"))

  const screenShareTrack = useHMSStore(
    selectScreenShareByPeerID(teacherPeer?.id)
  );

  // useEffect(() => {
  //   toast.success(`ScreenShare track is now: ${screenShareTrack?.id}`);
  //   console.warn(`ScreenShare track is now: ${screenShareTrack?.id}`);
  // }, [screenShareTrack]);

  // console.log("Screenshare Track");
  // console.log(screenShareTrack);
  // console.log(screenShareTrack?.id);

  const localPeer = useHMSStore(selectLocalPeer);

  // Auto-disable video if local connection is degraded (Optimized for low-end devices/poor network)
  useEffect(() => {
    if (!isConnected || !localPeer?.videoTrack || !isLocalVideoEnabled) return;

    const isDegraded = globalStores.curTalkingParty.isTrackDegraded(
      localPeer.videoTrack
    );

    if (isDegraded) {
      logger.log("Local track degraded, starting resource-saving timer...");
      const timer = setTimeout(() => {
        // Re-check degradation before taking action
        if (
          globalStores.curTalkingParty.isTrackDegraded(localPeer.videoTrack) &&
          isLocalVideoEnabled
        ) {
          logger.log(
            "Connection highly unstable, auto-disabling video to preserve audio."
          );
          if (toggleVideo) {
            toggleVideo();
            toast(
              "Connection unstable. Camera turned off to keep audio working.",
              {
                icon: "📡",
                duration: 5000,
              }
            );
          }
        }
      }, isMobileOrTablet() ? 4000 : 8000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [
    isConnected,
    localPeer?.videoTrack,
    isLocalVideoEnabled,
    // We remove the size dependency and rely on the observer re-render to re-evaluate isDegraded
  ]);

  // const studentVideo = useVideo({
  //   trackId: localPeer?.videoTrack,
  // });

  // const { videoRef: teacherCameraTrackRef } = useVideo({
  //   trackId: teacherPeer?.videoTrack,
  // });
  // const { videoRef: teacherScreenShareTrackRef } = useVideo({
  //   trackId: screenShareTrack?.id,
  // });

  // const video123 = {
  //   trackId: screenShareTrack?.id ?? teacherPeer?.videoTrack,
  // };

  // console.log("Which track to use:", video123);

  // const teacherVideoRef = screenShareTrack?.id
  //   ? teacherScreenShareTrackRef
  //   : teacherCameraTrackRef;

  // console.log({ teacherVideoRef });

  // console.log(
  //   "Which Track for student:",
  //   teacherScreenShared && screenShareTrack?.id
  // );

  // const { videoRef: teacherVideoRef } = useVideo(video123);

  // iOS audio initialisation – uses a single shared AudioContext instead of
  // creating a new one on every call (which leaked resources on iPads).
  useEffect(() => {
    if (!isIOS()) return;

    const isChromeIpad = isChromeOnIOS();
    if (isChromeIpad) {
      logger.log("🔊 iOS AUDIO: Chrome on iOS detected – user agent:", navigator.userAgent);
    }

    // Unblock HMS audio once (safe to call repeatedly but we only need it once)
    hmsActions.unblockAudio();

    // Try to unlock the shared audio context immediately
    unlockAudio();

    // On user gesture, unlock audio again (iOS often requires gesture)
    const handleUserGesture = () => {
      logger.log("🔊 iOS AUDIO: User gesture – unlocking audio");
      hmsActions.unblockAudio();
      unlockAudio();
    };

    document.addEventListener("touchstart", handleUserGesture, { once: true });
    document.addEventListener("click", handleUserGesture, { once: true });

    return () => {
      document.removeEventListener("touchstart", handleUserGesture);
      document.removeEventListener("click", handleUserGesture);
    };
  }, [hmsActions]);

  useEffect(
    () =>
      logger.log("Current Room:", globalStores.curTalkingParty.talkingParty.id),
    [globalStores.curTalkingParty.talkingParty.id]
  );

  useEffect(() => {
    if (initialMuteDone) return;
    if (!globalStores.curTalkingParty.room) return;

    // iOS Safari requires user gesture for media permissions
    // Delay initial mute to ensure proper permission handling
    const performInitialMute = async () => {
      try {
        if (isSafari() && isIOS()) {
          // Wait a bit longer for Safari to properly initialize
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        await globalStores.curTalkingParty.toggleMicrophone(false);
        setInitialMuteDone(true);
        logger.log('Initial mute completed successfully');
      } catch (error) {
        logger.error('Failed to perform initial mute:', error);
        // Retry once after a delay
        setTimeout(async () => {
          try {
            await globalStores.curTalkingParty.toggleMicrophone(false);
            setInitialMuteDone(true);
          } catch (retryError) {
            logger.error('Retry initial mute failed:', retryError);
          }
        }, 2000);
      }
    };

    performInitialMute();
  }, [globalStores.curTalkingParty.room]);

  useEffect(() => {
    logger.log("Current Talking Party:", toJS(globalStores.curTalkingParty));
  }, [globalStores.curTalkingParty.room]);

  const toggleCamera = async () => {
    if (!toggleVideo) {
      throw new Error("You do not have permission to unmute video");
    }

    // For Safari on iOS, ensure we have permissions before toggling (non-blocking)
    if (isSafari() && isIOS()) {
      try {
        const hasPermissions = await requestMediaPermissions(true);
        if (!hasPermissions) {
          toast.error("Camera permission required. Please allow camera access.");
          return;
        }
      } catch (error) {
        logger.error('Camera permission check failed:', error);
        // Continue anyway to avoid blocking the toggle
      }
    }

    toggleVideo();
  };

  const toggleMic = async () => {
    if (!toggleAudio) {
      throw new Error("You do not have permission to unmute audio");
    }

    // Ensure the shared AudioContext is running before toggling mic
    if (isIOS()) {
      await resumeSharedAudioContext();
    }

    // For Safari on iOS, ensure we have permissions before toggling
    if (isSafari() && isIOS()) {
      try {
        const hasPermissions = await requestMediaPermissions(true);
        if (!hasPermissions) {
          toast.error("Microphone permission required. Please allow microphone access.");
          return;
        }
      } catch (error) {
        logger.error("Microphone permission check failed:", error);
      }
    }

    toggleAudio();
  };

  const setImagePreviewOption = useCallback(
    (option?: keyof EngagementPanelStore["assets"]) => {
      setCurrentOptionImagePreview(option);
    },
    []
  );

  const curEPanelID = globalStores.curEPanel.engagementPanel.id;
  const supabaseEPanelID = globalStores.curTalkingParty.talkingParty.currentEngagementPanel;

  //Consolidated panel loading logic that checks cache
  const loadPanel = useCallback((id: number) => {
    logger.log("loadPanel: Attempting to load panel ID:", id);
    
    // Update local store state immediately to prevent "ping-pong" reverts
    if (globalStores.curTalkingParty.talkingParty.currentEngagementPanel !== id) {
      globalStores.curTalkingParty.talkingParty.currentEngagementPanel = id;
    }

    const preloadedPanel = globalStores.curModule.engagementPanelsLoaded.find(
      (p) => p.engagementPanel.id === id
    );

    if (preloadedPanel) {
      logger.log("loadPanel: Found preloaded panel for ID:", id);
      globalStores.curEPanel.initFromInput(preloadedPanel, { skipRefresh: true });
    } else {
      logger.log("loadPanel: Fetching panel from DB for ID:", id);
      globalStores.curEPanel.init(id);
    }
  }, []);

  const currentEngagementPanelIdFromHMS = useHMSStore(
    selectSessionStore("currentEngagementPanelId")
  );
  const currentModuleIdFromHMS = useHMSStore(
    selectSessionStore("currentModuleId")
  );
  const showAnswerFromHMS = useHMSStore(
    selectSessionStore("showAnswer")
  );

  const lastHMSPanelIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      currentEngagementPanelIdFromHMS &&
      typeof currentEngagementPanelIdFromHMS === "number" &&
      currentEngagementPanelIdFromHMS !== lastHMSPanelIdRef.current
    ) {
      LOG_EP(
        "HMS Sync: currentEngagementPanelId changed",
        currentEngagementPanelIdFromHMS
      );
      lastHMSPanelIdRef.current = currentEngagementPanelIdFromHMS;
      if (
        currentEngagementPanelIdFromHMS !==
        globalStores.curEPanel.engagementPanel.id
      ) {
        loadPanel(currentEngagementPanelIdFromHMS);
      }
    }
  }, [currentEngagementPanelIdFromHMS, loadPanel]);

  // Supabase fallback for the current panel — mirrors the module effect further
  // below (which loads curModule from talkingParty.module). Without this, the
  // panel is driven ONLY by the 100ms session store, so when that delivery
  // stalls the student loads no panel at all — nothing from the start, in every
  // class — even though the module still loads via its own Supabase fallback.
  // talkingParty.currentEngagementPanel is kept fresh by the background
  // pullDetails() that runs on every TalkingPartyRoom realtime update. loadPanel
  // is idempotent (guards on id + preload cache), so this coexists with the HMS
  // path without double-loading or ping-pong.
  useEffect(() => {
    if (
      typeof supabaseEPanelID === "number" &&
      supabaseEPanelID !== globalStores.curEPanel.engagementPanel.id
    ) {
      loadPanel(supabaseEPanelID);
    }
  }, [supabaseEPanelID, loadPanel]);

  const lastHMSModuleIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      currentModuleIdFromHMS &&
      typeof currentModuleIdFromHMS === "number" &&
      currentModuleIdFromHMS !== lastHMSModuleIdRef.current
    ) {
      LOG_EP("HMS Sync: currentModuleId changed", currentModuleIdFromHMS);
      lastHMSModuleIdRef.current = currentModuleIdFromHMS;
      if (currentModuleIdFromHMS !== globalStores.curModule.module.id) {
        globalStores.curModule.init(currentModuleIdFromHMS);
      }
    }
  }, [currentModuleIdFromHMS]);

  const lastHMSShowAnswerRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (showAnswerFromHMS !== undefined && showAnswerFromHMS !== lastHMSShowAnswerRef.current) {
      LOG_EP("HMS Sync: showAnswer changed", showAnswerFromHMS);
      lastHMSShowAnswerRef.current = showAnswerFromHMS;
      globalStores.curTalkingParty.talkingParty.showAnswerForEngagementPanel = !!showAnswerFromHMS;
    }
  }, [showAnswerFromHMS]);

  useEffect(() => {
    globalStores.curTalkingParty.setHMSActions(hmsActions);
  }, [hmsActions]);

  const hasObservedRef = useRef(false);
  useEffect(() => {
    if (isConnected && !hasObservedRef.current) {
      // Observe standard HMS session keys and our custom sync keys (GFW resilient)
      hmsActions.sessionStore.observe([
        "currentEngagementPanel",
        "showAnswerForEngagementPanel",
        "currentEngagementPanelId",
        "currentModuleId",
        "showAnswer",
      ]);
      hasObservedRef.current = true;
    } else if (!isConnected) {
      hasObservedRef.current = false;
    }
  }, [hmsActions, isConnected]);

  // Use Supabase Realtime ONLY for internal room details updates, not UI triggers
  useEffect(() => {
    const roomId = globalStores.curTalkingParty.talkingParty.id;
    LOG_EP("Realtime subscription effect run", { isConnected, roomId });
    if (!isConnected || !roomId) return;

    // We no longer rely on Supabase for instant UI triggers.
    // We only keep the channel for background updates if necessary.
    const channel = supabase
      .channel(`room-${roomId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "TalkingPartyRoom",
          filter: `id=eq.${roomId}`,
        },
        (payload) => {
          LOG_EP("Supabase: Room details updated in background");
          // Background sync only, won't trigger UI reverts anymore
          globalStores.curTalkingParty.pullDetails();
        }
      )
      .subscribe();
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [isConnected, globalStores.curTalkingParty.talkingParty.id, rtResubscribeTick]);

  // Health check: every 7s verify realtime channel is subscribed; if not, resubscribe
  useEffect(() => {
    const roomId = globalStores.curTalkingParty.talkingParty.id;
    if (!isConnected || !roomId) return;

    const checkHealth = () => {
      const channel = channelRef.current as any;
      if (!channel) {
        LOG_EP("Health check: no channel, scheduling resubscribe", { roomId });
        setRtResubscribeTick((t) => t + 1);
        return;
      }

      const status =
        channel.state ?? channel.status ?? channel.subscribeStatus ?? "unknown";
      const isHealthy = status === "SUBSCRIBED" || status === "joined";

      if (!isHealthy) {
        LOG_EP("Health check: channel unhealthy, resubscribing", {
          roomId,
          status,
        });
        supabase.removeChannel(channel);
        channelRef.current = null;
        setRtResubscribeTick((t) => t + 1);
      } else {
        LOG_EP("Health check: channel healthy", { roomId, status });
      }
    };

    const intervalId = setInterval(checkHealth, REALTIME_HEALTH_INTERVAL_MS);
    checkHealth();

    return () => clearInterval(intervalId);
  }, [isConnected, globalStores.curTalkingParty.talkingParty.id]);

  const showAnswerForEngagementPanelFromSupabase =
    globalStores.curTalkingParty.talkingParty.showAnswerForEngagementPanel;
  const showAnswerForEngagementPanel =
    showAnswerForEngagementPanelFromSupabase === true;

  useEffect(() => {
    if (showAnswerForEngagementPanel) {
      console.log("Show Answer State Detected (Supabase):", {
        supabase: showAnswerForEngagementPanelFromSupabase,
      });
    }
  }, [showAnswerForEngagementPanel, showAnswerForEngagementPanelFromSupabase]);

  const localPeerMetadata: HMSTalkingPartyPeerMetadata = useHMSStore(
    selectPeerMetadata(localPeer?.id ?? "")
  ) ?? {};

  const setRaiseHand = useCallback(
    async (setIsHandRaiseTo: boolean) => {
      const newMetadata: HMSTalkingPartyPeerMetadata = {
        ...localPeerMetadata,
        isHandRaised: setIsHandRaiseTo,
      };
      await hmsActions.changeMetadata(newMetadata);
    },
    [hmsActions, localPeerMetadata]
  );

  const toggleRaiseHand = useCallback(async () => {
    const newHandRaiseStatus = !localPeerMetadata.isHandRaised;
    await setRaiseHand(newHandRaiseStatus);
  }, [hmsActions, localPeerMetadata]);

  const setEngagementPanelAnswer = useCallback(
    async (answer: EngagementPanelResultString) => {
      const activeEPanelID = globalStores.curEPanel.engagementPanel.id;
      if (!activeEPanelID) {
        console.error("No active engagement panel ID to set answer for!");
        return;
      }

      const { engagementPanelResults = {} } = localPeerMetadata;

      const newEngagementPanelResults = {
        ...engagementPanelResults,
        [activeEPanelID]: answer,
      };

      const newMetadata: HMSTalkingPartyPeerMetadata = {
        ...localPeerMetadata,
        engagementPanelResults: newEngagementPanelResults,
      };

      await hmsActions.changeMetadata(newMetadata);
    },
    [hmsActions, localPeerMetadata, globalStores.curEPanel.engagementPanel.id]
  );

  const setFeeling = useCallback(
    async (feeling: string) => {
      const newMetadata: HMSTalkingPartyPeerMetadata = {
        ...localPeerMetadata,
        feeling,
      };

      // console.warn(newMetadata);
      await hmsActions.changeMetadata(newMetadata);
    },
    [hmsActions, localPeerMetadata]
  );

  useEffect(() => {
    // console.log("running this useeffect");
    const feeling = globalStores.curTalkingParty.feeling;
    // if (isConnected && feeling) {
    //   if (localPeerMetadata.feeling !== feeling) {
    //     setFeeling(feeling!)
    //     // const setFeelingPromise = setFeeling(feeling!);
    //     // toast.promise(setFeelingPromise, {
    //     //   success: `Feeling of ${feeling} loaded!`,
    //     //   loading: `Updating feeling to ${feeling}`,
    //     //   error: (e) => e.message,
    //     // });
    //   }
    // } else {
    //   // console.log("setFeeling aborted: not connected yet.");
    // }

    if (isConnected && feeling && localPeerMetadata.feeling !== feeling) {
      //update peer-metadata on feeling
      setFeeling(feeling!);

      //remove feeling after 7 mins
      const timeoutDuration = 7 * 60 * 1000; //7min*60s*1000ms
      const timer = setTimeout(() => {
        setFeeling("");
      }, timeoutDuration);
      return () => clearTimeout(timer);
    }
  }, [hmsActions, isConnected, globalStores.curTalkingParty.feeling]);

  const toggleMoreMenu = () => {
    setOpenMoreMenu(!openMoreMenu);
  };

  useEffect(() => {
    if (
      globalStores.curTalkingParty.talkingParty.module &&
      globalStores.curTalkingParty.talkingParty.module !==
        globalStores.curModule.module.id
    ) {
      globalStores.curModule
        .init(globalStores.curTalkingParty.talkingParty.module);
    }
  }, [globalStores.curTalkingParty.talkingParty.module]);

  //set RaiseHand to false when person first joins (actually useless)
  // useEffect(() => {
  //   // globalStores.curTalkingParty.setHandRaised(false);
  //   if (isConnected) {
  //     setRaiseHand(false);
  //   }
  // }, [isConnected]);

  useEffect(() => {
    isConnected &&
      toast.success(
        `${
          localPeerMetadata.isHandRaised
            ? "Hand raised"
            : "Hand no longer raised"
        }`
      );
  }, [localPeerMetadata.isHandRaised]);

  useEffect(() => {
    setQuizToggle(true);
  }, [globalStores.curEPanel.engagementPanel.id]);

  //TODO: Change the participant number
  useEffect(() => {
    const run = async () => {
      //If the lesson is not defined then we can use the querystring, else set the query string
      if (!globalStores.curTalkingParty.talkingParty.id) {
        return navigate("/student/dashboard");
      }

      if (!globalStores.curTalkingParty.twilioToken) {
        return navigate("/student/dashboard");
      }

      // Pre-join: ensure shared audio context is ready on iOS
      if (isIOS()) {
        await resumeSharedAudioContext();
      }

      try {
        const room = await globalStores.curTalkingParty.joinRoom();
        if (!room) {
          setJoinError("Failed to join classroom. No session created.");
          return;
        }
        logger.log("Successfully joined room");
      } catch (error: any) {
        logger.error("Error during joinRoom:", error);
        setJoinError(error.message || "Unknown error during join");
        return;
      }

      // Post-join: unblock audio for iOS and resume shared context
      if (isIOS()) {
        hmsActions.unblockAudio();
        await unlockAudio();
      }
    };

    run();
  }, [globalStores.curTalkingParty]);

  //provides a toast whenever microphone state changes
  useEffect(() => {
    isConnected &&
      toast.success(`Microphone ${isLocalAudioEnabled ? "On" : "Off"}`);
  }, [isLocalAudioEnabled]);

  //provides a toast whenever camera state changes
  useEffect(() => {
    isConnected &&
      toast.success(`Camera ${isLocalVideoEnabled ? "On" : "Off"}`);
  }, [isLocalVideoEnabled]);

  //for dealing with raise hand event
  useEffect(() => {
    hmsActions.ignoreMessageTypes(["LOWER_HAND"]);
  }, [hmsActions, globalStores.curTalkingParty]);

  useCustomEvent({
    type: "LOWER_HAND",
    onEvent: () => {
      setRaiseHand(false);
      toast.success("Your hand is being lowered by the teacher");
    },
  });

  useCustomEvent({
    type: "MUTE_ALL_STUDENTS",
    onEvent: () => {
      toast.success("Teacher has muted all students.");
    },
  });

  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices();

  const cycleToNextVideoDevice = useCallback(() => {
    if (allDevices.videoInput) {
      const curVideoInputID = selectedDeviceIDs.videoInput;

      const indexOfCurrentDevice = allDevices.videoInput.findIndex(
        ({ deviceId }) => curVideoInputID === deviceId
      );

      const indexOfNextDevice =
        (indexOfCurrentDevice + 1) % allDevices.videoInput.length;
      const { deviceId, label } = allDevices.videoInput[indexOfNextDevice];

      toast.promise(
        updateDevice({
          deviceId,
          deviceType: DeviceType.videoInput,
        }),
        {
          success: `Video Device successfully switched to ${label}`,
          loading: `Switching Video Device to ${label}.`,
          error: (e) => e.message,
        }
      );
    }
  }, [allDevices, selectedDeviceIDs, updateDevice]);

  const getCurSelectedAnswer = useCallback((): EngagementPanelResultString => {
    const activeID = globalStores.curEPanel.engagementPanel.id;
    if (!activeID) {
      return undefined;
    }
    const selection = localPeerMetadata?.engagementPanelResults?.[activeID];
    return selection;
  }, [globalStores.curEPanel.engagementPanel.id, localPeerMetadata?.engagementPanelResults]);
  return (
    <>
      <Modal open={openMoreMenu} onClose={() => setOpenMoreMenu(false)}>
        <MoreMenu />
      </Modal>
      {isReconnecting && (
        <div className="fixed inset-0 z-[100] bg-brij-Mud bg-opacity-80 flex flex-col items-center justify-center text-brij-White p-10 text-center">
          <div className="w-16 h-16 border-4 border-brij-Peach border-t-transparent rounded-full animate-spin mb-4" />
          <h2 className="text-heading2 font-bold mb-2">Connection Unstable</h2>
          <p className="text-large opacity-80 max-w-md">
            We are automatically trying to reconnect you to the classroom. Please stay on this page.
          </p>
          {showRestartButton && (
            <button
              onClick={handleManualRestart}
              className="mt-6 px-6 py-2 bg-brij-Orange text-white rounded-lg font-bold hover:bg-opacity-90 transition-all shadow-lg"
            >
              Restart Connection Now
            </button>
          )}
        </div>
      )}
      {!isConnected && !isReconnecting && (
        <div className="fixed inset-0 z-[100] bg-brij-Mud flex flex-col items-center justify-center text-brij-White p-10 text-center">
          {!joinError ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="scale-150">
                <BrijLoadingIndicator />
              </div>
              <h2 className="text-heading3 font-bold mb-2">Joining classroom...</h2>
            </div>
          ) : (
            <>
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-heading2 font-bold mb-2">Failed to Join</h2>
              <p className="text-large opacity-80 max-w-md mb-6">
                Couldn't establish a connection to the classroom.
              </p>
              <div className="bg-brij-Black bg-opacity-30 p-4 rounded-lg mb-6 text-xsmall font-mono break-all max-w-lg">
                Error: {joinError}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setJoinError(null);
                    window.location.reload();
                  }}
                  className="px-8 py-3 bg-brij-Orange text-white rounded-lg font-bold hover:bg-opacity-90 transition-all shadow-lg"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate("/student/dashboard")}
                  className="px-8 py-3 bg-brij-Grey bg-opacity-20 text-white rounded-lg font-bold hover:bg-opacity-30 transition-all"
                >
                  Back to Dashboard
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <div className="flex flex-col w-full h-full bg-brij-Default pb-4 sm:pb-[64px] overflow-clip relative">
        <div className="relative flex flex-col sm:flex-row w-full h-full">
          <div className="relative bg-brij-Black flex-grow grid place-items-center pt-20">
            {/* TITLE */}
            <div className="absolute object-cover top-0 left-0 px-2 sm:p-6 w-full from-brij-Black bg-gradient-to-b rounded-xl z-10">
              <div className="inline-flex flex-col gap-1">
                <div className="text-small sm:text-heading2 text-brij-White font-medium drop-shadow-xl">
                  {teacherPeer?.name} {curEPanelID ? "> " : ""}
                  {curEPanelID
                    ? globalStores.curModule.level.name
                    : ""}
                  {curEPanelID ? ": " : ""}
                  {curEPanelID
                    ? globalStores.curModule.topic.name
                    : ""}
                </div>
                <div className="text-xsmall sm:text-large text-brij-White drop-shadow-xl">
                  {curEPanelID
                    ? globalStores.curModule.module.name
                    : ""}
                </div>
              </div>
            </div>

            {/* PRIMARY FEED (BIG) */}
            {teacherPeer && (
              <PrimaryFeed
                screenShareTrackID={screenShareTrack?.id}
                cameraTrackID={teacherPeer?.videoTrack}
              />
            )}
            {/* SECONDARY FEED (BOTTOM LEFT) + AVATARS SHELF */}
            <div className="px-5 absolute inset-x-0 -bottom-[50px] origin-bottom flex flex-row">
              {/* SECONDARY FEED (BOTTOM LEFT) + LOCAL PEER AVATAR*/}
              <div className="flex flex-col w-[300px] h-full items-center gap-4">
                <SecondaryFeed
                  teacherCameraTrackID={teacherPeer?.videoTrack}
                  studentCameraTrackID={localPeer?.videoTrack}
                  screenShareTrackID={screenShareTrack?.id}
                  cycleToNextVideoDevice={cycleToNextVideoDevice}
                  numberOfCameraDevices={allDevices?.videoInput?.length ?? 0}
                />
                <AvatarGridItem participant={localPeer} />
              </div>
              {/*THE REST OF THE AVATARS*/}

              <AvatarsShelf
                studentPeers={studentPeers}
                localPeer={localPeer!}
              />
            </div>
            <ImagePreviewModal
              imageSrc={
                currentOptionImagePreview
                  ? globalStores.curEPanel.assets[
                      `${currentOptionImagePreview}`
                    ]
                  : undefined
              }
              imageUrl={
                currentOptionImagePreview
                  ? {
                      questionFile: globalStores.curEPanel.assetUrls.question,
                      optionAFile: globalStores.curEPanel.assetUrls.answerA,
                      optionBFile: globalStores.curEPanel.assetUrls.answerB,
                      optionCFile: globalStores.curEPanel.assetUrls.answerC,
                      optionDFile: globalStores.curEPanel.assetUrls.answerD,
                    }[currentOptionImagePreview]
                  : undefined
              }
            />
          </div>
          <div className="relative h-full aspect-9/18 sm:w-auto">
            <EngagementPanelComponent
              mode={showAnswerForEngagementPanel ? "SHOW_ANSWER" : "ANSWERING"}
              onAnswerSelected={async (newAns) => {
                if (isAnswering) return;

                // 1. Optimistic Update: Update Peer Metadata immediately for instant visual feedback
                await setEngagementPanelAnswer(newAns);

                setIsAnswering(true);
                try {
                  // 2. Background Save: Update the database
                  await globalStores.curEPanel.answerEngagementPanel(
                    newAns,
                    globalStores.curTalkingParty.talkingParty.id
                  );
                } catch (error) {
                  console.error("Failed to set answer:", error);
                  toast.error("Failed to save answer. Please try again.");
                  // Note: In a full implementation, we might revert setEngagementPanelAnswer(oldAns) here
                } finally {
                  setIsAnswering(false);
                }
              }}
              curSelectedAnswer={getCurSelectedAnswer()}
              setImagePreviewOption={setImagePreviewOption}
              isAnswering={isAnswering}
            />
            <Options
              localPeerMetadata={localPeerMetadata}
              isLocalVideoEnabled={isLocalVideoEnabled}
              isLocalAudioEnabled={isLocalAudioEnabled}
              toggleRaiseHand={toggleRaiseHand}
              toggleCamera={toggleCamera}
              toggleMic={toggleMic}
              toggleMoreMenu={toggleMoreMenu}
            />
          </div>
        </div>
        {/* <DebugAssetShelf engagementPanels={globalStores.curModule.engagementPanels} /> */}
      </div>
    </>
  );
}

function DebugAssetShelf({ engagementPanels }: { engagementPanels: any[] }) {
  if (!engagementPanels || engagementPanels.length === 0) return null;

  const getPublicUrl = (path: any) => {
    if (!path) return null;
    // Handle both string and JSON-wrapped paths
    const filePath = typeof path === "string" ? path : path?.path;
    if (!filePath) return null;
    
    // Consistent with engagementPanel store logic: strip extension and add .webp
    const webpPath = filePath.replace(/\.(png|jpe?g|gif)$/i, "") + ".webp";
    
    return supabase.storage.from("teaching-assets").getPublicUrl(webpPath).data?.publicUrl;
  };

  return (
    <div className="fixed top-2 right-2 z-[100] bg-black bg-opacity-70 p-2 rounded-lg max-h-[300px] overflow-y-auto w-[150px] border border-white border-opacity-30 pointer-events-auto">
      <p className="text-[10px] text-white font-bold mb-1 border-b border-white border-opacity-20 pb-1">
        Module Assets (WebP)
      </p>
      <div className="flex flex-col gap-2">
        {engagementPanels.map((panel, idx) => {
          const urls = [
            getPublicUrl(panel.qnsFile),
            getPublicUrl(panel.optionAFile),
            getPublicUrl(panel.optionBFile),
            getPublicUrl(panel.optionCFile),
            getPublicUrl(panel.optionDFile),
          ].filter(Boolean);

          if (urls.length === 0) return null;

          return (
            <div key={panel.id ?? idx} className="flex flex-col gap-1">
              <p className="text-[8px] text-gray-300">Panel {idx + 1} (ID: {panel.id})</p>
              <div className="flex flex-wrap gap-1">
                {urls.map((url, uIdx) => (
                  <div key={uIdx} className="relative group">
                    <img
                      src={url!}
                      alt={`asset-${uIdx}`}
                      className="w-8 h-8 object-cover rounded border border-gray-500 hover:scale-[3] transition-transform origin-top-right z-[101]"
                      onError={(e) => {
                        (e.target as any).src = "https://via.placeholder.com/32?text=ERR";
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AvatarsShelf({
  studentPeers,
  localPeer,
}: {
  studentPeers: HMSPeer[];
  localPeer: HMSPeer;
}) {
  return (
    <div className="flex relative w-full scale-75 sm:scale-100 items-end ">
      <div className="flex flex-row w-full justify-evenly">
        {studentPeers.map((student: HMSPeer) => {
          //LEFT OFF: Still need to set the child and shit
          //TODO: Register audio and video feeds

          if (student.id === localPeer.id) {
            return <></>;
          }
          return <AvatarGridItem key={student.id} participant={student} />;
          // return (
          //   <>
          //     <AvatarGridItem key={student.id + "1"} participant={student} />
          //     <AvatarGridItem key={student.id + "2"} participant={student} />
          //     <AvatarGridItem key={student.id + "3"} participant={student} />
          //   </>
          // );
        })}
      </div>
    </div>
  );
}

const Options = observer(({
  localPeerMetadata,
  isLocalVideoEnabled,
  isLocalAudioEnabled,
  toggleRaiseHand,
  toggleCamera,
  toggleMic,
  toggleMoreMenu,
}: {
  localPeerMetadata: HMSTalkingPartyPeerMetadata;
  isLocalVideoEnabled: boolean;
  isLocalAudioEnabled: boolean;
  toggleRaiseHand: () => Promise<void>;
  toggleCamera: () => void;
  toggleMic: () => void;
  toggleMoreMenu: () => void;
}) => {
  const globalStores = useGlobalStore();
  const localPeer = useHMSStore(selectLocalPeer);

  return (
    <div className="absolute inset-x-0 -bottom-[58px] origin-bottom scale-75 sm:scale-100 z-20">
      <div className="flex justify-center gap-3">
        <div
          onClick={toggleRaiseHand}
          className="relative cursor-pointer h-[52px] w-[52px] drop-shadow-lg fill-brij-White bg-brij-Blue rounded-2xl p-3"
        >
          {!localPeerMetadata.isHandRaised && (
            <div className={"absolute inset-0 grid place-items-center"}>
              <div className="bg-brij-White h-1 w-10 rotate-45" />
            </div>
          )}
          <IconHand />
        </div>
        <div
          onClick={toggleCamera}
          className="relative cursor-pointer h-[52px] w-[52px] drop-shadow-lg fill-brij-White bg-brij-Peach rounded-2xl p-3"
        >
          {!isLocalVideoEnabled && (
            <div className={"absolute inset-0 grid place-items-center"}>
              <div className="bg-brij-White h-1 w-10 rotate-45" />
            </div>
          )}
          <IconRecord />
        </div>
        <div
          onClick={toggleMic}
          className={`relative cursor-pointer h-[52px] w-[52px] drop-shadow-lg fill-brij-White bg-brij-Orange rounded-2xl p-3 ${
            globalStores.curTalkingParty.isTrackDegraded(localPeer?.audioTrack)
              ? "animate-pulse border-4 border-white"
              : ""
          }`}
        >
          {!isLocalAudioEnabled && (
            <div className={"absolute inset-0 grid place-items-center"}>
              <div className="bg-brij-White h-1 w-10 rotate-45" />
            </div>
          )}
          <IconMic />
        </div>
        <div
          onClick={toggleMoreMenu}
          className="cursor-pointer h-[52px] w-[52px] drop-shadow-lg fill-brij-White bg-brij-DarkGrey rounded-2xl p-3 stroke-brij-White"
        >
          <IconMoreHorizontal />
        </div>
      </div>
    </div>
  );
});

function PrimaryFeed({
  cameraTrackID,
  screenShareTrackID,
}: {
  cameraTrackID?: string;
  screenShareTrackID?: string;
}) {
  // console.log("TeacherFeed is being rerendered");
  // if (screenShareTrackID) {
  //   return <TeacherVideo videoTrack={screenShareTrackID} />;
  // }

  // return <TeacherVideo videoTrack={cameraTrackID} />;
  return (
    <>
      {cameraTrackID && !screenShareTrackID ? (
        <PrimaryVideo videoTrack={cameraTrackID} />
      ) : (
        <></>
      )}

      {screenShareTrackID ? (
        <PrimaryVideo videoTrack={screenShareTrackID} />
      ) : (
        <></>
      )}
    </>
  );
}

function PrimaryVideo({ videoTrack }: { videoTrack?: string }) {
  const globalStores = useGlobalStore();
  const isDegraded = globalStores.curTalkingParty.isTrackDegraded(videoTrack);

  return (
    <div className="relative w-full h-full max-h-[80vh]">
      <VideoElement videoTrack={videoTrack} />
      {isDegraded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-20 pointer-events-none">
          <div className="w-12 h-12 border-4 border-brij-Peach border-t-transparent rounded-full animate-spin mb-4" />
          <span className="text-large font-bold text-white drop-shadow-md">
            Connection Unstable
          </span>
        </div>
      )}
    </div>
  );
}

// Memoized video element to prevent expensive re-attaching of tracks
// when only the "isDegraded" overlay changes.
const VideoElement = memo(({ videoTrack }: { videoTrack?: string }) => {
  const { videoRef } = useVideo({
    trackId: videoTrack,
  });

  useEffect(() => {
    if (isChromeOnIOS() && videoTrack) {
      logger.log("🔊 CHROME IPAD DEBUG: Primary video track attached:", videoTrack);
    }
  }, [videoTrack]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="object-contain w-full h-full"
    />
  );
});

function SecondaryFeed({
  teacherCameraTrackID,
  studentCameraTrackID,
  screenShareTrackID,
  cycleToNextVideoDevice,
  numberOfCameraDevices,
}: {
  teacherCameraTrackID?: string;
  studentCameraTrackID?: string;
  screenShareTrackID?: string;
  cycleToNextVideoDevice: () => void;
  numberOfCameraDevices?: number;
}) {
  return (
    <div className="relative">
      {!screenShareTrackID ? (
        <SecondaryVideo videoTrack={studentCameraTrackID} />
      ) : (
        <></>
      )}
      {screenShareTrackID ? (
        <SecondaryVideo videoTrack={teacherCameraTrackID} />
      ) : (
        <></>
      )}

      {!screenShareTrackID && numberOfCameraDevices! > 1 && (
        <div
          className="absolute object-cover bg-brij-DarkGrey rounded-full p-[0.15rem] top-1 left-1 z-30 aspect-square hover:cursor-pointer"
          onClick={cycleToNextVideoDevice}
        >
          <RefreshIcon className="text-brij-White" />
        </div>
      )}
    </div>
  );
}

function SecondaryVideo({ videoTrack }: { videoTrack?: string }) {
  const globalStores = useGlobalStore();
  const isDegraded = globalStores.curTalkingParty.isTrackDegraded(videoTrack);

  return (
    <div className="relative">
      <SecondaryVideoElement videoTrack={videoTrack} />
      {isDegraded && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black bg-opacity-20 rounded-xl pointer-events-none">
          <div className="w-6 h-6 border-2 border-brij-Peach border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

const SecondaryVideoElement = memo(({ videoTrack }: { videoTrack?: string }) => {
  const { videoRef } = useVideo({
    trackId: videoTrack,
  });

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="bg-brij-DarkGrey flex relative object-contain w-auto h-[120px] shadow-xl rounded-xl sm:scale-100 origin-bottom-left -scale-x-100 aspect-video z-20"
    />
  );
});

// function TeacherVideo({ peer }: { peer?: HMSPeer }) {
//   console.log(`Video re-rendered with ID: ${peer?.videoTrack}`);

//   const { videoRef } = useVideo({
//     trackId: peer?.videoTrack,
//   });

//   console.log(`videoRef: ${videoRef}`);

//   return (
//     <video
//       poster={loading}
//       ref={videoRef}
//       className="rounded-xl drop-shadow-xl max-w-full max-h-full"
//       autoPlay
//       muted
//       playsInline
//     />
//   );
// }

function MoreMenu() {
  const [openEndClassFeedback, setOpenEndClassFeedback] = useState(false);

  return (
    <>
      <Modal
        open={openEndClassFeedback}
        onClose={() => setOpenEndClassFeedback(false)}
      >
        <EndClassFeedback />
      </Modal>
      <BaseCard>
        <div className="inline-flex flex-col gap-3 w-full">
          <p>Menu</p>
          <ButtonPrimary onClick={() => setOpenEndClassFeedback(true)}>
            Leave Class
          </ButtonPrimary>
        </div>
      </BaseCard>
    </>
  );
}

//TODO: Set end class time when teacher ends class
export function EndClassFeedback() {
  const globalStores = useGlobalStore();
  const navigate = useNavigate();

  const _handleRatingSelected = async (newRating: number) => {
    await globalStores.curTalkingParty.submitChildFeedback(newRating);
    await globalStores.curTalkingParty.leaveRoom();
    navigate("/student/dashboard");
  };

  const handleRatingSelected = (newRating: number | undefined) => {
    if (newRating === undefined) {
      return toast.error("Please select a rating first!");
    }
    toast.promise(_handleRatingSelected(newRating), {
      success: "Rating Submitted",
      loading: "Submitting Rating",
      error: (e) => e.message,
    });
  };

  return (
    <div className="inline-flex w-[317px] flex-col items-center justify-center gap-8 rounded-3xl bg-[rgba(255,254,252,1)] px-12 py-8 drop-shadow-lg">
      <p className="text-center font-['Poppins'] text-xl font-medium leading-[1.4] text-[rgba(64,52,52,1)]">
        How was class today?
      </p>
      <div className="flex w-full">
        <StarRatingForm onChangeRating={handleRatingSelected} />
      </div>
      <HappyFriendsSVG />
    </div>
  );
}
